// Real-Time Voice InputArea for OpenAI Realtime API
import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../assets/styles/InputArea.css';
import { VoiceSettings, VoiceState } from '../types/Message';
import EmojiPicker from './EmojiPicker';

interface InputAreaProps {
  onSendMessage: (message: string, responseMode: 'auto' | 'text_only' | 'voice_only' | 'speech_to_text_only', voiceId?: string) => void;
  onFileUpload: (file: File, description?: string) => void;
  // Optional batched form — when present and multiple non-audio files are
  // staged, InputArea calls this once instead of looping onFileUpload per
  // file. Lets the server experience the batch as a single turn (one
  // user bubble, one cross-referencing vision pass) rather than N
  // disconnected uploads.
  onFilesUpload?: (files: File[], description?: string) => void;
  onAudioFileUpload: (audioData: string, filename: string, format: string) => void;
  disabled: boolean;
  voiceEnabled: boolean;
  voiceSettings: VoiceSettings;
  voiceState: VoiceState;
  onInputModeChange: (mode: 'text' | 'voice') => void;
}

interface PendingFile {
  file: File;
  preview?: string;
  type: 'image' | 'document' | 'audio';
}

// localStorage keys for text-to-voice (read-aloud) preferences
const TEXT_TO_VOICE_KEY = 'emcog_text_to_voice';
const TTS_VOICE_KEY = 'emcog_tts_voice';
const DEFAULT_TTS_VOICE = 'ash';

const InputArea: React.FC<InputAreaProps> = ({
  onSendMessage,
  onFileUpload,
  onFilesUpload,
  onAudioFileUpload,
  disabled,
  voiceEnabled,
  voiceSettings,
  voiceState,
  onInputModeChange
}) => {
  const [message, setMessage] = useState('');
  const [inputMode, setInputMode] = useState<'text' | 'voice'>('text');
  const [isDragging, setIsDragging] = useState(false);
  // Array so the paperclip/drop accumulates files instead of replacing the
  // staged one. The previous single-slot state silently clobbered the first
  // picture when the user added a second.
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  // Text-to-voice (read-aloud) state, persisted in localStorage
  const [textToVoice, setTextToVoice] = useState<boolean>(
    () => localStorage.getItem(TEXT_TO_VOICE_KEY) === 'true'
  );
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(
    () => localStorage.getItem(TTS_VOICE_KEY) || DEFAULT_TTS_VOICE
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef(0);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  const resetTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '24px';
    }
  }, []);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  // Detect if mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Handle keyboard events
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      if (isMobile) {
        // Mobile: Enter inserts space
        const textarea = textareaRef.current;
        if (textarea) {
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const newValue = message.substring(0, start) + ' ' + message.substring(end);
          setMessage(newValue);

          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = start + 1;
            adjustTextareaHeight();
          }, 0);
        }
      } else {
        // Desktop: Enter sends message
        handleSend();
      }
    }
  };

  // Handle mode toggle between text and live voice conversation.
  // Entering voice mode tells the parent, which starts the realtime
  // session (WebSocket /voice); leaving stops it. Server-side VAD handles
  // turn-taking — no hold-to-record, no spacebar push-to-talk.
  const handleModeToggle = useCallback((mode: 'text' | 'voice') => {
    if (mode === inputMode) return;
    console.log('🔄 Mode toggle:', mode);
    setInputMode(mode);
    onInputModeChange(mode);
  }, [inputMode, onInputModeChange]);

  // Handle send message
  const handleSend = () => {
    // Can send if there's a message OR at least one pending file
    if ((!message.trim() && pendingFiles.length === 0) || disabled) return;

    let responseMode = voiceSettings.responseMode;

    if (responseMode === 'auto') {
      // Check if text-to-voice is enabled for text mode
      if (textToVoice) {
        responseMode = 'voice_only'; // Text input with voice response
      } else {
        responseMode = 'text_only';
      }
    }

    // If there are pending files, send each one. The message (if any) goes
    // along as the shared description on every upload. Audio routes through
    // onAudioFileUpload; everything else through onFileUpload.
    if (pendingFiles.length > 0) {
      const description = message.trim() || undefined;

      // Audio routes through its own handler (FileReader + audioData
      // signature is different). Everything else batches.
      const audioFiles = pendingFiles.filter(pf => pf.type === 'audio');
      const otherFiles = pendingFiles.filter(pf => pf.type !== 'audio');

      for (const pf of audioFiles) {
        const reader = new FileReader();
        const capturedFile = pf.file;
        reader.onload = () => {
          const audioData = reader.result as string;
          onAudioFileUpload(audioData, capturedFile.name, capturedFile.type);
        };
        reader.readAsDataURL(capturedFile);
      }

      if (otherFiles.length > 0) {
        if (otherFiles.length > 1 && onFilesUpload) {
          // Batched path — one WebSocket message, one user bubble, one
          // experience. Description applies to the whole batch.
          onFilesUpload(otherFiles.map(pf => pf.file), description);
        } else {
          // Single file (or no batch handler wired) — legacy per-file
          // upload. For N==1 this is functionally identical to the batch path.
          for (const pf of otherFiles) {
            onFileUpload(pf.file, description);
          }
        }
      }

      // Revoke any object URLs we created for preview
      for (const pf of pendingFiles) {
        if (pf.preview) URL.revokeObjectURL(pf.preview);
      }
      setPendingFiles([]);
      setMessage('');
      resetTextareaHeight();
      return;
    }

    // Normal text message - pass voiceId when text-to-voice is enabled
    onSendMessage(message.trim(), responseMode, textToVoice ? selectedVoiceId : undefined);
    setMessage('');
    resetTextareaHeight();
  };

  // Cancel a specific pending file by index
  const handleCancelFile = (index: number) => {
    setPendingFiles(prev => {
      const target = prev[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Validate and stage a batch of files for preview. Shared between the
  // file-input and drag-drop paths so validation rules stay in one place.
  // Files that fail validation are reported via alert and skipped; valid
  // ones are appended to the existing staged list.
  const stageFiles = (files: FileList | File[]) => {
    const supportedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const supportedDocTypes = ['application/pdf', 'text/plain', 'text/markdown',
                              'application/json', 'text/csv'];
    const supportedAudioTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/m4a', 'audio/webm'];

    const maxSizes: { [key: string]: number } = {
      'image': 10 * 1024 * 1024,
      'audio': 25 * 1024 * 1024,
      'document': 50 * 1024 * 1024,
      'default': 25 * 1024 * 1024
    };

    const accepted: PendingFile[] = [];
    const rejected: string[] = [];

    for (const file of Array.from(files)) {
      let fileCategory: 'image' | 'document' | 'audio' = 'document';
      if (file.type.startsWith('image/')) fileCategory = 'image';
      else if (file.type.startsWith('audio/')) fileCategory = 'audio';

      const maxSize = maxSizes[fileCategory] || maxSizes['default'];
      if (file.size > maxSize) {
        const sizeMB = Math.round(maxSize / 1024 / 1024);
        rejected.push(`"${file.name}" — too large (max ${sizeMB}MB for ${fileCategory})`);
        continue;
      }

      const isSupported = supportedImageTypes.includes(file.type) ||
                          supportedDocTypes.includes(file.type) ||
                          supportedAudioTypes.includes(file.type);
      if (!isSupported) {
        rejected.push(`"${file.name}" — unsupported type "${file.type || 'unknown'}"`);
        continue;
      }

      accepted.push({
        file,
        preview: fileCategory === 'image' ? URL.createObjectURL(file) : undefined,
        type: fileCategory
      });
    }

    if (rejected.length > 0) {
      alert(`Some files couldn't be added:\n\n${rejected.join('\n')}`);
    }

    if (accepted.length > 0) {
      setPendingFiles(prev => [...prev, ...accepted]);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  // Handle file selection - stages file(s) for preview instead of immediate upload
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      stageFiles(e.target.files);
    }
    // Reset so selecting the same file twice in a row still fires change
    e.target.value = '';
  };

  // 🔥 Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      stageFiles(files);
    }
    // stageFiles defined in closure with setPendingFiles — safe to reference
    // without adding it to deps since setPendingFiles is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the component unmounts while in voice mode, make sure the parent
  // tears the realtime session down.
  const inputModeRef = useRef(inputMode);
  inputModeRef.current = inputMode;
  const onInputModeChangeRef = useRef(onInputModeChange);
  onInputModeChangeRef.current = onInputModeChange;

  useEffect(() => {
    return () => {
      if (inputModeRef.current === 'voice') {
        onInputModeChangeRef.current('text');
      }
    };
  }, []); // Empty deps - only run on unmount

  // Listen for voice selection changes from the Sidebar voice picker
  useEffect(() => {
    const handleVoiceChanged = (event: Event) => {
      const voiceId = (event as CustomEvent<string>).detail;
      if (voiceId) {
        setSelectedVoiceId(voiceId);
      }
    };

    const handleTextToVoiceChanged = (event: Event) => {
      const enabled = (event as CustomEvent<boolean>).detail;
      setTextToVoice(enabled);
    };

    window.addEventListener('emcog-tts-voice-changed', handleVoiceChanged);
    window.addEventListener('emcog-text-to-voice-changed', handleTextToVoiceChanged);

    return () => {
      window.removeEventListener('emcog-tts-voice-changed', handleVoiceChanged);
      window.removeEventListener('emcog-text-to-voice-changed', handleTextToVoiceChanged);
    };
  }, []);

  // Focus textarea when switching to text mode
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && inputMode === 'text') {
      textarea.focus();
    }
  }, [inputMode]);

  // Reset height when message is cleared
  useEffect(() => {
    if (message === '') {
      resetTextareaHeight();
    }
  }, [message, resetTextareaHeight]);

  // Live voice status line — driven by the useVoice hook's connection state
  const voiceStatus = voiceState.error
    ? { icon: '⚠️', text: voiceState.error }
    : voiceState.isSpeaking
      ? { icon: '🔊', text: 'Speaking...' }
      : voiceState.isConnected
        ? { icon: '🎙️', text: 'Live voice — just talk' }
        : { icon: '⚪', text: 'Connecting...' };

  // Can send if there's a message OR at least one pending file
  const canSend = (message.trim() || pendingFiles.length > 0) && !disabled;

  return (
    <div
      ref={wrapperRef}
      className={`input-area-wrapper ${isDragging ? 'drag-over' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="input-area-container">
        {/* File Preview — one row per staged file */}
        {pendingFiles.length > 0 && (
          <div className="file-preview-container">
            {pendingFiles.map((pf, idx) => (
              <div className="file-preview" key={`${pf.file.name}-${idx}`}>
                {pf.type === 'image' && pf.preview ? (
                  <img src={pf.preview} alt="Preview" className="file-preview-image" />
                ) : (
                  <div className="file-preview-icon">
                    {pf.type === 'audio' ? '🎵' : '📄'}
                  </div>
                )}
                <div className="file-preview-info">
                  <span className="file-preview-name">{pf.file.name}</span>
                  <span className="file-preview-size">
                    {(pf.file.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <button
                  className="file-preview-cancel"
                  onClick={() => handleCancelFile(idx)}
                  title="Remove file"
                  type="button"
                  aria-label={`Remove ${pf.file.name}`}
                >
                  ✕
                </button>
              </div>
            ))}
            <span className="file-preview-hint">
              {pendingFiles.length === 1
                ? 'Add a message or send as-is'
                : `${pendingFiles.length} files staged — add a message or send as-is`}
            </span>
          </div>
        )}

        {/* Mode Toggle Buttons */}
        {voiceEnabled && (
          <div className="input-mode-indicator">
            <div className="mode-indicators-row">
              {/* Text-to-Voice toggle (left side, only in text mode) */}
              {inputMode === 'text' && (
                <button
                  type="button"
                  className={`text-to-voice-toggle ${textToVoice ? 'active' : ''}`}
                  onClick={() => {
                    const newValue = !textToVoice;
                    setTextToVoice(newValue);
                    localStorage.setItem(TEXT_TO_VOICE_KEY, String(newValue));
                    window.dispatchEvent(new CustomEvent<boolean>('emcog-text-to-voice-changed', {
                      detail: newValue
                    }));
                  }}
                  disabled={disabled}
                  title={textToVoice ? 'Voice responses enabled - click to disable' : 'Enable voice responses'}
                  aria-label={textToVoice ? 'Disable voice responses' : 'Enable voice responses'}
                >
                  {textToVoice ? '🔊' : '🔇'}
                </button>
              )}

              {/* Mode toggle buttons */}
              <div className="mode-indicators">
                <button
                  type="button"
                  className={`mode-indicator ${inputMode === 'text' ? 'active' : ''}`}
                  onClick={() => handleModeToggle('text')}
                  disabled={disabled}
                  aria-label="Switch to text input mode"
                >
                  ⌨️ Text
                </button>
                <button
                  type="button"
                  className={`mode-indicator ${inputMode === 'voice' ? 'active' : ''}`}
                  onClick={() => handleModeToggle('voice')}
                  disabled={disabled}
                  aria-label="Switch to voice mode"
                  title="Live voice conversation"
                >
                  🎙️ Voice
                </button>
              </div>
            </div>
            {inputMode === 'voice' && (
              <span className="voice-hint">
                {voiceState.isConnected ? '🟢 Connected — just talk' : '⚪ Connecting...'}
              </span>
            )}
          </div>
        )}

        {/* Main Input Area */}
        <div className="input-area">
          {inputMode === 'voice' ? (
            /* Live Voice Mode UI — realtime conversation, server-side VAD
               handles turn-taking. No buttons to hold; just talk. */
            <div className={`ptt-container ${voiceState.isConnected ? 'connected' : 'disconnected'}`}>
              <div className="ptt-status">
                <div className="ptt-ready">
                  <span className="ready-icon">{voiceStatus.icon}</span>
                  <span className="ready-text">{voiceStatus.text}</span>
                </div>
              </div>

              <button
                className="disconnect-button"
                onClick={() => handleModeToggle('text')}
                title="End the live voice conversation and switch to text"
                type="button"
                aria-label="End voice conversation"
              >
                End voice
              </button>
            </div>
          ) : (
            /* Text Mode UI - Normal Input */
            <div className="input-container">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                accept="image/*,audio/*,.pdf,.txt,.md,.csv,.json"
                aria-label="Upload files for consciousness to experience"
                title="Share one or more images, documents, or audio files"
              />

              <button
                className="file-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                title="Share an image, document, or audio file"
                type="button"
                aria-label="Upload file for consciousness to experience"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
              </button>

              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={
                  disabled
                    ? "Connecting to consciousness..."
                    : "Share your thoughts..."
                }
                disabled={disabled}
                rows={1}
                aria-label="Message input"
              />

              <div className="input-actions">
                {/* Emoji Picker - Desktop only, positioned on right side */}
                <EmojiPicker
                  onEmojiSelect={(emoji) => {
                    setMessage((prev) => prev + emoji);
                    textareaRef.current?.focus();
                  }}
                  disabled={disabled}
                />

                <button
                  className="send-button"
                  onClick={handleSend}
                  disabled={!canSend}
                  title="Send message"
                  type="button"
                  aria-label="Send message"
                >
                  {/* Arrow icon handled by CSS */}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InputArea;
